import styled from "@emotion/styled";
import { Box, Button, Paper, Typography } from "@mui/material";
import { FC, useRef, useState } from "react";
import CustomInput from "../../../../_common/CustomInput";
import emptyImage from '../../../../../assets/img/emptyImage.png';
import DatePicker from "react-datepicker";
import { pl } from "date-fns/locale";
import { format } from "date-fns";
import CustomButton from "../../../../_common/CustomButton";
import LoadingAnimation from "../../../../_common/Loading";
import imageSliderController, { IAddSlideDto } from "../../../../../Api/imageSliderController";
import { useToast } from "../../../../../hooks/useToast";

const DateBox = styled(Box)`
    width: 100%;    
`

const DateDiv = styled.div`
    display: flex;
    justify-content: center;
`

const DeleteButton = styled(Button)`
    margin-top: 10px;
    width: 100%;
`

const DropZone = styled.div`
    border: 2px dashed #ccc;
    padding: 20px;
    height: 100px;
    box-sizing: border-box;
    border-radius: 10px;
    text-align: center;
    cursor: pointer;
    margin-bottom: 20px;
`;


interface NewSlideComponentProps {
    onHideForm: () => void;
    onRefreshSlider: () => void;
}

const NewSlideComponent: FC<NewSlideComponentProps> = ({
    onHideForm,
    onRefreshSlider,
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [link, setLink] = useState<string>('');
    const [image, setImage] = useState<File | null>(null);
    const [publishDate, setPublishDate] = useState<Date | null>(null);
    const [isPublishDateOpen, setIsPublishDateOpen] = useState(false);
    const [expireDate, setExpireDate] = useState<Date | null>(null);
    const [isExpireDateOpen, setIsExpireDateOpen] = useState(false);
    const [removeDate, setRemoveDate] = useState<Date | null>(null);
    const [isRemoveDateOpen, setIsRemoveDateOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const toast = useRef(useToast()).current;


    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files) as File[];
        if (files.length > 0) setImage(files[0]);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []) as File[];
        if (files.length > 0) setImage(files[0]);
    };


    const handleAddSlide = async () => {
        setLoading(true)
        if (!image) {
            toast.error('Proszę dodać zdjęcie')
            return setLoading(false)
        }
        const data: IAddSlideDto = {
            image: image as File,
            link,
            expiresAt: expireDate ?? null,
            publishedAt: publishDate ?? null,
            deletedAt: removeDate ?? null,
        };
        const res = await imageSliderController.addImage(data);
        if (!res.ok) {
            toast.error(res.message)
        }
        setLoading(false);
        if (!res.ok) return;
        onHideForm()
        onRefreshSlider()
        toast.success(res.message);
    }


    if (loading) return <LoadingAnimation />

    return (
        <Box sx={{
            p: { xs: 2, md: 3 },
            border: '1px solid #dfe5f1',
            bgcolor: '#f8faff',
            mb: 3,
            borderRadius: 3,
        }}>
            <Typography variant="h6" fontWeight={850} mb={2}>Nowy slajd</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' }, gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                    {image ?
                        <img
                            src={URL.createObjectURL(image)}
                            alt={image.name}
                            style={{ width: '100%', height: 180, objectFit: 'contain', marginBottom: 16 }}
                        />
                        :
                        <img
                            src={emptyImage}
                            alt="Brak zdjęcia"
                            style={{ width: '100%', height: 180, marginBottom: 16, objectFit: 'contain' }}
                        />

                    }

                    <DropZone
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Przeciągnij zdjęcie tutaj lub kliknij
                    </DropZone>
                    <input
                        type="file"
                        multiple
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />
                </Box>
                <Box sx={{ width: '100%' }}>
                    <CustomInput
                        label="Link"
                        value={link}
                        setValue={setLink}
                        mode="text"
                        variant='filled'
                        sx={{ width: '100%' }}
                    />
                </Box>
            </Box>
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
                    gap: 1,
                    py: 2,
                }}>
                    <>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'flex-start',
                                flexDirection: 'column',
                                borderRadius: 2,
                                mb: 2,
                                width: '100%',
                                height: '100%',
                                m: 0,
                                boxShadow: 'none'
                            }}
                        >
                            <Button
                                fullWidth
                                variant="outlined"
                                sx={{ mb: 2 }}
                                onClick={() => {
                                    setIsPublishDateOpen(!isPublishDateOpen);
                                }}
                                tabIndex={-1}
                                onPointerDown={e => e.stopPropagation()}
                            >
                                {publishDate !== null
                                    ? `Data publikacji: ${format(publishDate, "dd MMMM yyyy", { locale: pl })}`
                                    : "Wybierz datę publikacji"}
                            </Button>
                            {isPublishDateOpen && (
                                <DateBox>
                                    <DateDiv tabIndex={-1} onPointerDown={e => e.stopPropagation()}>
                                        <DatePicker
                                            selected={publishDate}
                                            onChange={((date: any) => { setPublishDate(date); setIsPublishDateOpen(false) })}
                                            locale={pl}
                                            dateFormat="dd.MM.yyyy"
                                            inline
                                        />
                                    </DateDiv>
                                    {publishDate &&
                                        <DeleteButton
                                            color="error"
                                            variant="contained"
                                            onClick={() => setPublishDate(null)}
                                            onPointerDown={e => e.stopPropagation()}
                                        >
                                            Usuń datę
                                        </DeleteButton>}
                                </DateBox>
                            )}
                        </Paper>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'flex-start',
                                flexDirection: 'column',
                                borderRadius: 2,
                                mb: 2,
                                width: '100%',
                                height: '100%',
                                m: 0,
                                boxShadow: 'none'
                            }}
                        >
                            <Button
                                color="secondary"
                                fullWidth
                                variant="outlined"
                                sx={{ mb: 2 }}
                                onClick={() => {
                                    setIsExpireDateOpen(!isExpireDateOpen);
                                }}
                                tabIndex={-1}
                                onPointerDown={e => e.stopPropagation()}
                            >
                                {expireDate !== null
                                    ? `Data wygaśnięcia: ${format(expireDate, "dd MMMM yyyy", { locale: pl })}`
                                    : "Wybierz datę wygaśnięcia"}
                            </Button>
                            {isExpireDateOpen && (
                                <DateBox>
                                    <DateDiv tabIndex={-1} onPointerDown={e => e.stopPropagation()}>
                                        <DatePicker
                                            selected={expireDate}
                                            onChange={(date: any) => {
                                                setExpireDate(date);
                                                setIsExpireDateOpen(false);
                                                if (removeDate === null) setRemoveDate(date);
                                            }}
                                            locale={pl}
                                            dateFormat="dd.MM.yyyy"
                                            inline
                                        />
                                    </DateDiv>
                                    {expireDate && <DeleteButton
                                        color="error"
                                        variant="contained"
                                        onClick={() => setExpireDate(null)}
                                        onPointerDown={e => e.stopPropagation()}
                                    >
                                        Usuń datę
                                    </DeleteButton>}
                                </DateBox>
                            )}
                        </Paper>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'flex-start',
                                flexDirection: 'column',
                                borderRadius: 2,
                                mb: 2,
                                width: '100%',
                                height: '100%',
                                m: 0,
                                boxShadow: 'none'
                            }}
                        >
                            <Button
                                color="error"
                                fullWidth
                                variant="outlined"
                                sx={{ mb: 2 }}
                                onClick={() => {
                                    setIsRemoveDateOpen(!isRemoveDateOpen);
                                }}
                                tabIndex={-1}
                                onPointerDown={e => e.stopPropagation()}
                            >
                                {removeDate !== null
                                    ? `Data usunięcia: ${format(removeDate, "dd MMMM yyyy", { locale: pl })}`
                                    : "Wybierz datę usunięcia"}
                            </Button>
                            {isRemoveDateOpen && (
                                <DateBox>
                                    <DateDiv tabIndex={-1} onPointerDown={e => e.stopPropagation()}>
                                        <DatePicker
                                            selected={removeDate}
                                            onChange={(date: any) => { setRemoveDate(date); setIsRemoveDateOpen(false) }}
                                            locale={pl}
                                            dateFormat="dd.MM.yyyy"
                                            inline
                                        />
                                    </DateDiv>
                                    {removeDate && <DeleteButton
                                        color="error"
                                        variant="contained"
                                        onClick={() => setRemoveDate(null)}
                                        onPointerDown={e => e.stopPropagation()}
                                    >
                                        Usuń datę
                                    </DeleteButton>}
                                </DateBox>
                            )}
                        </Paper>
                    </>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'end', mt: 2 }}>
                <CustomButton
                    text="Dodaj slajd"
                    onClick={handleAddSlide}
                />
            </Box>
        </Box >
    )
}

export default NewSlideComponent;
